import { OidcService } from '@hawaii-framework/oidc-implicit-core';
import { AxiosPromise } from 'axios';
import { DEFAULT_OIDC_SCOPES } from 'config/app.config';
import RequiredError from 'error/RequiredError';
import * as url from 'url';

import { ApiResponse, query, queryOneApp, rest } from './api';
import ApiFactory from './api-factory';
import {
  getInvoiceListQuery,
  getInvoiceQuery,
  getPaymentLinkQuery,
  getPaymentMethodsQuery,
  getPaymentResponseQuery,
  oneAppGetInvoiceChargesQuery,
  oneAppGetInvoicesQuery,
} from './invoice.query';
import {
  HashPaymentResponse,
  InvoicePayment,
  InvoicePaymentMethod,
  InvoicePaymentMethodEnum,
  InvoicePaymentStatus,
  InvoiceSummary,
  OneAppInvoiceCharges,
  OneAppInvoiceList,
  PaymentLink,
  TokenPaymentResponse,
} from './invoice.types';

// TODO: getInvoicePdf is omitted here since the endpoint isn't called like the others but a link
// to the PDF is opened in a new window in the invoices container, remove it when the last
// call from the InvoiceApi has been implemented. Other calls may need to be omitted since the original interface
// pretends we're returning an AxiosPromise while we're not and TS will warn about incorrect implementation
interface InvoiceApiInterface {
  getPaymentMethods(billingCustomerId: string, invoiceId: string): ApiResponse<InvoicePaymentMethod[]>;
  getPaymentLink(
    billingCustomerId: string,
    invoiceId: string,
    paymentMethod: InvoicePaymentMethodEnum
  ): ApiResponse<InvoicePayment>;
  getInvoiceList(billingCustomerId: string, months: number): ApiResponse<InvoiceSummary[]>;
  getInvoice(billingCustomerId: string, invoiceId: string): ApiResponse<InvoiceSummary>;
  isInvoicePdfAvailable(billingCustomerId: string, invoiceId: string, fullInvoice: boolean): ApiResponse<never>;
  getPaymentResponse(variables: TokenPaymentResponse | HashPaymentResponse): ApiResponse<InvoicePaymentStatus>;
  invoicePaymentLinkCreditCard(
    billingCustomerId: string,
    invoiceId: string,
    options?: Record<string, unknown>
  ): AxiosPromise<PaymentLink>;
  invoicePaymentLinkIdeal(
    billingCustomerId: string,
    invoiceId: string,
    options?: Record<string, unknown>
  ): AxiosPromise<PaymentLink>;
}

interface OneAppInvoiceApiInterface {
  oneAppGetInvoices(
    billingCustomerId: string,
    billingArrangementId: string,
    latestOnly: boolean
  ): ApiResponse<OneAppInvoiceList>;
  oneAppGetInvoiceCharges(
    billingCustomerId: string,
    billingArrangementId: string,
    billingInvoiceId: string,
    invoiceCloseDate: string
  ): ApiResponse<OneAppInvoiceCharges>;
}

const oneAppInvoiceApi: OneAppInvoiceApiInterface = {
  oneAppGetInvoiceCharges(
    billingCustomerId: string,
    billingArrangementId: string,
    billingInvoiceId: string,
    invoiceCloseDate: string
  ): ApiResponse<OneAppInvoiceCharges> {
    if (billingCustomerId === null || billingCustomerId === undefined) {
      throw new RequiredError(
        'billingCustomerId',
        'Required parameter billingCustomerId was null or undefined when calling getInvoiceCharges.'
      );
    }

    if (billingArrangementId === null || billingArrangementId === undefined) {
      throw new RequiredError(
        'billingArrangementId',
        'Required parameter billingArrangementId was null or undefined when calling getInvoiceCharges.'
      );
    }

    if (billingInvoiceId === null || billingInvoiceId === undefined) {
      throw new RequiredError(
        'billingInvoiceId',
        'Required parameter billingInvoiceId was null or undefined when calling getInvoiceCharges.'
      );
    }

    if (invoiceCloseDate === null || invoiceCloseDate === undefined) {
      throw new RequiredError(
        'invoiceCloseDate',
        'Required parameter invoiceCloseDate was null or undefined when calling getInvoiceCharges.'
      );
    }

    const token = OidcService.getStoredToken({ scopes: DEFAULT_OIDC_SCOPES });

    return queryOneApp<{ getInvoiceCharges: OneAppInvoiceCharges }>({
      query: oneAppGetInvoiceChargesQuery,
      variables: { billingCustomerId, billingArrangementId, billingInvoiceId, invoiceCloseDate },
      headers: {
        Authorization: token ? OidcService.getAuthHeader(token) : undefined,
      },
      name: 'getInvoiceCharges',
    }).then(({ getInvoiceCharges }) => ({ data: getInvoiceCharges }));
  },
  oneAppGetInvoices(
    billingCustomerId: string,
    billingArrangementId: string,
    latestOnly = false
  ): ApiResponse<OneAppInvoiceList> {
    if (billingCustomerId === null || billingCustomerId === undefined) {
      throw new RequiredError(
        'billingCustomerId',
        'Required parameter billingCustomerId was null or undefined when calling getInvoices.'
      );
    }

    if (billingArrangementId === null || billingArrangementId === undefined) {
      throw new RequiredError(
        'billingArrangementId',
        'Required parameter billingArrangementId was null or undefined when calling getInvoices.'
      );
    }

    const token = OidcService.getStoredToken({ scopes: DEFAULT_OIDC_SCOPES });

    return queryOneApp<{ getInvoices: OneAppInvoiceList }>({
      query: oneAppGetInvoicesQuery,
      variables: { billingCustomerId, billingArrangementId, latestOnly },
      headers: {
        Authorization: token ? OidcService.getAuthHeader(token) : undefined,
      },
      name: 'getInvoices',
    }).then(({ getInvoices }) => {
      return { data: getInvoices };
    });
  },
};

const InvoiceApi: InvoiceApiInterface = {
  invoicePaymentLinkCreditCard(
    billingCustomerId: string,
    invoiceId: string,
    options: Record<string, unknown> = {}
  ): AxiosPromise {
    // verify required parameter 'billingCustomerId' is not null or undefined
    if (billingCustomerId === null || billingCustomerId === undefined) {
      throw new RequiredError(
        'billingCustomerId',
        'Required parameter billingCustomerId was null or undefined when calling invoicePaymentLinkCreditCard.'
      );
    }
    // verify required parameter 'invoiceId' is not null or undefined
    if (invoiceId === null || invoiceId === undefined) {
      throw new RequiredError(
        'invoiceId',
        'Required parameter invoiceId was null or undefined when calling invoicePaymentLinkCreditCard.'
      );
    }

    const localVarQueryParameter = {};

    const localVarPath =
      `/selfservice/rest/v1/billing-customers/{billingCustomerId}/invoices/{invoiceId}/payment-links/credit-card`
        .replace(`{${'billingCustomerId'}}`, encodeURIComponent(String(billingCustomerId)))
        .replace(`{${'invoiceId'}}`, encodeURIComponent(String(invoiceId)));
    const localVarUrlObj = url.parse(localVarPath, true);
    const method = 'GET';

    const contentType = 'application/json';
    let data;

    localVarUrlObj.query = {
      ...localVarUrlObj.query,
      ...localVarQueryParameter,
      ...(options.query && typeof options.query === 'object' ? options.query : {}),
    };
    return ApiFactory.getInstance()
      .getApi()
      .api({
        ...options,
        url: url.format(localVarUrlObj),
        method,
        data,
        headers: {
          'Content-Type': contentType,
        },
      });
  },

  invoicePaymentLinkIdeal(
    billingCustomerId: string,
    invoiceId: string,
    options: Record<string, unknown> = {}
  ): AxiosPromise {
    // verify required parameter 'billingCustomerId' is not null or undefined
    if (billingCustomerId === null || billingCustomerId === undefined) {
      throw new RequiredError(
        'billingCustomerId',
        'Required parameter billingCustomerId was null or undefined when calling invoicePaymentLinkIdeal.'
      );
    }
    // verify required parameter 'invoiceId' is not null or undefined
    if (invoiceId === null || invoiceId === undefined) {
      throw new RequiredError(
        'invoiceId',
        'Required parameter invoiceId was null or undefined when calling invoicePaymentLinkIdeal.'
      );
    }

    const localVarQueryParameter = {};

    const localVarPath =
      `/selfservice/rest/v1/billing-customers/{billingCustomerId}/invoices/{invoiceId}/payment-links/ideal`
        .replace(`{${'billingCustomerId'}}`, encodeURIComponent(String(billingCustomerId)))
        .replace(`{${'invoiceId'}}`, encodeURIComponent(String(invoiceId)));
    const localVarUrlObj = url.parse(localVarPath, true);
    const method = 'GET';

    const contentType = 'application/json';
    let data;

    localVarUrlObj.query = {
      ...localVarUrlObj.query,
      ...localVarQueryParameter,
      ...(options.query && typeof options.query === 'object' ? options.query : {}),
    };

    return ApiFactory.getInstance()
      .getApi()
      .api({
        ...options,
        url: url.format(localVarUrlObj),
        method,
        data,
        headers: {
          'Content-Type': contentType,
        },
      });
  },

  isInvoicePdfAvailable(billingCustomerId, invoiceId, fullInvoice) {
    return rest({
      url: `/invoice/pdf/${billingCustomerId}/${invoiceId}`,
      params: {
        fullInvoice,
      },
      method: 'GET',
      responseType: 'blob',
    });
  },

  getPaymentResponse(variables: TokenPaymentResponse | HashPaymentResponse) {
    return query<{ getPaymentResponse: InvoicePaymentStatus }>({
      query: getPaymentResponseQuery,
      variables: { ...variables },
      name: 'getPaymentResponse',
    }).then(({ getPaymentResponse }) => ({ data: getPaymentResponse }));
  },

  getPaymentMethods(billingCustomerId, invoiceId) {
    return query<{ getPaymentMethods: InvoicePaymentMethod[] }>({
      query: getPaymentMethodsQuery,
      variables: { billingCustomerId, invoiceId },
      name: 'getPaymentMethods',
    }).then(({ getPaymentMethods }) => ({ data: getPaymentMethods }));
  },

  getPaymentLink(billingCustomerId, invoiceId, paymentMethod) {
    return query<{ getPaymentLink: PaymentLink }>({
      query: getPaymentLinkQuery,
      variables: { billingCustomerId, invoiceId, paymentMethod },
      name: 'getPaymentLink',
    }).then(({ getPaymentLink }) => ({ data: getPaymentLink }));
  },

  getInvoiceList(billingCustomerId, months) {
    return query<{ getInvoiceList: InvoiceSummary[] }>({
      query: getInvoiceListQuery,
      variables: { billingCustomerId, months },
      name: 'getInvoiceList',
    }).then(({ getInvoiceList }) => ({ data: getInvoiceList }));
  },

  getInvoice(billingCustomerId, invoiceId) {
    return query<{ getInvoice: InvoiceSummary }>({
      query: getInvoiceQuery,
      variables: { billingCustomerId, invoiceId },
      name: 'getInvoice',
    }).then(({ getInvoice }) => ({ data: getInvoice }));
  },
};

export { InvoiceApi, oneAppInvoiceApi };
